import { IconCopy } from "@tabler/icons-react"
import { type FC, useState } from "react"

import { type ThinEvent as EventType } from "@forento/core/models/event"
import { toDateTimeString } from "@forento/core/utilities/date"

import { DeleteIcon, EditIcon, UsersIcon } from "~/components/Icon"
import ProductListRow from "~/components/ProductListRow"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { query } from "~/utilities/trpc"

import EditModal from "./EditModal"
import UsersModal from "./UsersModal"

type Props = { event: EventType; onReload(): Promise<unknown> }
const Event: FC<Props> = ({ event, onReload }) => {
	const alert = useAlert()
	const toast = useToast()

	const [isEditOpen, setEditOpen] = useState(false)
	const [isUsersOpen, setUsersOpen] = useState(false)

	const remove = query.event.delete.useMutation({
		async onSuccess() {
			await onReload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to delete event. Please try again.")
		},
	})
	const duplicate = query.event.duplicate.useMutation({
		async onSuccess() {
			await onReload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to duplicate event. Please try again.")
		},
	})

	async function handleDuplicate() {
		const dialog = await alert.confirm(
			"Duplicate event",
			`Are you sure you want to duplicate the event **${event.title}**?`,
			{ isDestructive: false },
		)
		if (!dialog.result) return
		await duplicate.mutateAsync(event.id)
		dialog.close()
	}

	async function handleDelete() {
		const dialog = await alert.confirm(
			"Delete event",
			`Are you sure you want to delete the event **${event.title}**?`,
		)
		if (!dialog.result) return
		await remove.mutateAsync(event.id)
		dialog.close()
	}

	return (
		<>
			<EditModal eventId={event.id} isOpen={isEditOpen} onClose={() => setEditOpen(false)} onReload={onReload} />
			<UsersModal eventId={event.id} isOpen={isUsersOpen} onClose={() => setUsersOpen(false)} />
			<ProductListRow
				title={event.title}
				subtitle={
					<>
						<time className="text-light text-xs">{toDateTimeString(event.startDate)}</time>
						<div className="size-1 rounded-full bg-slate-300" />
						<div>{event.users.attendees.length} attending</div>
					</>
				}
				actions={[
					{ id: "users", tooltip: "Users", icon: UsersIcon, onClick: () => setUsersOpen(true) },
					{ id: "edit", tooltip: "Edit", icon: EditIcon, onClick: () => setEditOpen(true) },
					{ id: "duplicate", tooltip: "Duplicate", icon: IconCopy, onClick: handleDuplicate },
					{ id: "delete", tooltip: "Delete", icon: DeleteIcon, danger: true, onClick: handleDelete },
				]}
			/>
		</>
	)
}

export default Event
